import { motion } from '@eversports/react-components/design-tokens/motion'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import PyroForm from 'pyro-form'
import React, { useEffect, useMemo, useState } from 'react'

import { Localized } from '../../localization/react'
import {
  ConfirmWithOption,
  getCurrentTicketPrice,
  isPurchaseOptionAMembership,
  isPurchaseOptionAService,
  isPurchaseOptionATicket,
  ProductSelectionInitialValues,
  PurchaseOption,
} from '../../typings/Product'

import AssignedProductsList from './AssignedProductsList'
import { PrivateSessionBookingRequestAlert } from './PrivateSessionBookingRequestAlert'
import PurchaseOptionsList from './PurchaseOptionsList'

const getPurchasablePrice = (purchasable: PurchaseOption): number => {
  if (isPurchaseOptionAMembership(purchasable)) {
    return purchasable.paymentPlan.initial?.value ?? purchasable.paymentPlan.recurring.value
  }
  if (isPurchaseOptionATicket(purchasable)) {
    return getCurrentTicketPrice(purchasable).value
  }
  return purchasable.price.value
}

interface Props {
  initialValues: ProductSelectionInitialValues
  purchaseOptions: Array<PurchaseOption>
  assignedProducts: Array<ConfirmWithOption>
  onProductSelection: (settleWithId: string) => void
  isPrivateSessionBookingRequest: boolean
  isPayAtVenueAllowed: boolean
}

const ProductSelectionComponent: React.FC<React.PropsWithChildren<Props>> = ({
  initialValues,
  onProductSelection,
  purchaseOptions,
  assignedProducts,
  isPrivateSessionBookingRequest,
  isPayAtVenueAllowed,
}) => {
  const [selectedPurchasable, setSelectedPurchasable] = useState<PurchaseOption | null>(() => {
    if (!initialValues.settleWithId) return null
    return purchaseOptions.find((p) => p.id === initialValues.settleWithId) ?? null
  })
  const showPrivateSessionAlert =
    isPrivateSessionBookingRequest && selectedPurchasable && getPurchasablePrice(selectedPurchasable) > 0

  const orderedPurchaseOptions = useMemo(() => {
    const sortedServices = purchaseOptions
      .filter(isPurchaseOptionAService)
      .sort((a, b) => a.price.value - b.price.value)
    const sortedMemberships = purchaseOptions.filter(isPurchaseOptionAMembership).sort((a, b) => {
      const aValue = a.paymentPlan.initial ? a.paymentPlan.initial.value : a.paymentPlan.recurring.value
      const bValue = b.paymentPlan.initial ? b.paymentPlan.initial.value : b.paymentPlan.recurring.value
      return aValue - bValue
    })
    const sortedTickets = purchaseOptions
      .filter(isPurchaseOptionATicket)
      .sort((a, b) => getCurrentTicketPrice(a).value - getCurrentTicketPrice(b).value)

    return [...sortedServices, ...sortedMemberships, ...sortedTickets]
  }, [purchaseOptions])

  const trialOfferOptions = orderedPurchaseOptions.filter(
    (purchaseOption) => purchaseOption.__typename === 'Card' && purchaseOption.isTrialCard,
  )
  const orderedPurchaseOptionsWithoutTrialOffer = orderedPurchaseOptions.filter(
    (purchaseOption) => !trialOfferOptions.includes(purchaseOption),
  )

  const handleChange = (values: ProductSelectionInitialValues): void => {
    const purchasable = orderedPurchaseOptionsWithoutTrialOffer.find((p) => p.id === values.settleWithId) ?? null
    setSelectedPurchasable(purchasable)
    onProductSelection(values.settleWithId)
  }

  useEffect(() => {
    if (initialValues.settleWithId) return

    const hasOnlyOnePurchaseOption = purchaseOptions && purchaseOptions.length === 1 && assignedProducts.length === 0
    if (!hasOnlyOnePurchaseOption) return

    onProductSelection(purchaseOptions[0].id)
    initialValues.settleWithId = purchaseOptions[0].id
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ ease: 'easeInOut' }}
    >
      <Heading is="h3" marginBottom="wide">
        <Localized id="participation-options" />
      </Heading>
      <PyroForm initialValues={initialValues} onChange={handleChange}>
        <Flex flexDirection="column" verticalSpacing="wide">
          {assignedProducts.length > 0 && (
            <Flex flexDirection="column" data-testid="assigned-products-container">
              <Heading is="h4" variant="small">
                <Localized id="my-passes" />
              </Heading>
              <AssignedProductsList products={assignedProducts} />
            </Flex>
          )}
          {trialOfferOptions.length > 0 && (
            <Flex flexDirection="column" data-testid="purchasable-trial-offers-container">
              <Heading is="h4" variant="small">
                <Localized id="trial-offers" />
              </Heading>
              <PurchaseOptionsList products={trialOfferOptions} />
            </Flex>
          )}
          {orderedPurchaseOptionsWithoutTrialOffer.length > 0 && (
            <Flex flexDirection="column" data-testid="purchasable-products-container">
              <Heading is="h4" variant="small">
                {assignedProducts.length > 0 ? <Localized id="additional-pass" /> : <Localized id="new-pass" />}
              </Heading>
              {showPrivateSessionAlert && (
                <PrivateSessionBookingRequestAlert isPayAtVenueAllowed={isPayAtVenueAllowed} />
              )}
              <PurchaseOptionsList products={orderedPurchaseOptionsWithoutTrialOffer} />
            </Flex>
          )}
        </Flex>
      </PyroForm>
    </motion.div>
  )
}

export default ProductSelectionComponent
